import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../../services/api';
import { AssignedShares } from '../../../types/position';

/**
 * Shares from assignment — still holding? (glossary assigned_shares_confirmation).
 *
 * Shares assigned from a cash-secured put with no covered call opened on them
 * since. The app can't know whether you still hold them, so it asks: one tap
 * for "Yes", or "Sold" with your price (and the New York date, default today).
 * Until you answer they count as held and are flagged. Never assumed sold.
 */
const shortDate = (iso?: string | null) =>
  iso ? new Date(iso.slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';

const Row: React.FC<{ s: AssignedShares; onAnswered: () => void }> = ({ s, onAnswered }) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'ask' | 'sold'>('ask');
  const [price, setPrice] = useState('');
  const [day, setDay] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const priceNum = Number(price);
  const priceOk = price.trim() !== '' && Number.isFinite(priceNum) && priceNum > 0;

  const answer = async (stillHolding: boolean) => {
    if (busy || (!stillHolding && !priceOk)) return;
    setBusy(true);
    setError(null);
    try {
      await apiService.confirmAssignedShares(s.campaign_id, stillHolding
        ? { still_holding: true }
        : { still_holding: false, sold_price: priceNum, ...(day ? { sold_date: day } : {}) });
      onAnswered();
    } catch {
      setError("Couldn't save your answer. Try again.");
      setBusy(false);
    }
  };

  return (
    <li className={`asp-row ${s.needs_confirmation ? 'is-ask' : ''}`}>
      <span className="asp-what">
        <b>{s.symbol}</b> · {s.shares ?? '—'} sh assigned at {s.assigned_strike != null ? s.assigned_strike.toFixed(2) : '—'} on {shortDate(s.assigned_on)}
        <span className="asp-dim"> · price {s.stock_price != null ? s.stock_price.toFixed(2) : '—'}</span>
      </span>
      {s.needs_confirmation ? (
        mode === 'ask' ? (
          <span className="asp-act">
            <span className="asp-q">Still holding?</span>
            <button type="button" className="ck-btn" disabled={busy} onClick={() => answer(true)}>Yes</button>
            <button type="button" className="ck-btn" disabled={busy} onClick={() => setMode('sold')}>Sold…</button>
          </span>
        ) : (
          <span className="asp-act">
            <label>Sold at <input type="number" min="0" step="0.01" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} aria-label={`${s.symbol} sale price per share`} /></label>
            <label>on <input type="date" value={day} onChange={(e) => setDay(e.target.value)} aria-label="Sale date (New York); empty = today" /></label>
            <button type="button" className="ck-btn is-primary" disabled={busy || !priceOk} onClick={() => answer(false)}>Save</button>
            <button type="button" className="ck-btn" disabled={busy} onClick={() => setMode('ask')}>Cancel</button>
          </span>
        )
      ) : (
        <span className="asp-act">
          <span className="asp-dim">holding</span>
          {s.put_position_id && (
            <button type="button" className="ck-btn" onClick={() => navigate(`/analysis?symbol=${s.symbol}`, {
              state: { wheelContext: { positionId: s.put_position_id, ticker: s.symbol, assignmentStrike: s.assigned_strike, shares: s.shares } },
            })}>Sell a call</button>
          )}
        </span>
      )}
      {error && <span className="asp-err" role="alert">{error}</span>}
    </li>
  );
};

const AssignedSharesPanel: React.FC<{ shares: AssignedShares[] | undefined; onAnswered: () => void }> = ({ shares, onAnswered }) => {
  if (!shares || shares.length === 0) return null;
  const toAsk = shares.filter((s) => s.needs_confirmation).length;
  return (
    <section className="asp" aria-label="Shares from assignment">
      <div className="asp-h">
        Shares from assignment{toAsk > 0 && <> · <b>{toAsk} to confirm</b></>}
        <small>No covered call on them yet. They count as held until you answer.</small>
      </div>
      <ul>{shares.map((s) => <Row key={s.campaign_id} s={s} onAnswered={onAnswered} />)}</ul>
    </section>
  );
};

export default AssignedSharesPanel;
